import { Box } from '@mui/material';
import React from 'react';
import Colors from '../../utils/color';

const TYPE_LABEL: Record<string, string> = {
  hear_and_confirm: 'Hear & Confirm',
};

export const typeLabel = (type: string) => {
  if (TYPE_LABEL[type]) return TYPE_LABEL[type];
  const text = type.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** The section divider inside an assist card: 8px either side of the line. */
export const ASSIST_SECTION_SX = {
  mt: 1,
  pt: 1,
  borderTop: '1px solid rgba(255,255,255,0.12)',
} as const;

/** The card's right-hand column, beside the header: the countdown ring. */
export const ASSIST_SIDE_SX = {
  flex: '0 0 96px',
  boxSizing: 'border-box',
  borderLeft: '1px solid rgba(255,255,255,0.12)',
  pl: 1.5,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
} as const;

/**
 * The store code, never truncated: it may break only after an underscore
 * (`presto_staging_carlsjr_` / `brand_menu_qa_1`), so a long code wraps in its
 * own column beside the pill instead of running under the ring.
 */
const StoreCode = ({ code }: { code: string }) => (
  <>
    {code.split('_').map((part, index, parts) => (
      // eslint-disable-next-line react/no-array-index-key
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && (
          <>
            _<wbr />
          </>
        )}
      </React.Fragment>
    ))}
  </>
);

interface Props {
  type: string;
  restaurantCode: string;
  language: string;
  question: string;
}

/**
 * The top of the assist card: the type pill and the store, then the
 * question. Top-aligned; the store never truncates (it wraps at underscores,
 * beside the pill) and the question wraps.
 */
const AssistCardHeader = ({
  type,
  restaurantCode,
  language,
  question,
}: Props) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Box
      sx={{
        display: 'flex',
        // No wrap: a flex row would move the whole store code to a line of
        // its own before breaking it. The code wraps inside its own column,
        // beside the pill, at its underscores.
        alignItems: 'baseline',
        gap: 1,
      }}
    >
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          whiteSpace: 'nowrap',
          fontFamily: 'monospace',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          px: 1,
          py: 0.375,
          borderRadius: 99,
          backgroundColor: Colors.cerulean,
          color: Colors.daintree,
        }}
      >
        {typeLabel(type)}
      </Box>
      <Box
        component="span"
        data-testid="assist-store"
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          fontSize: 14,
          fontFamily: 'monospace',
          opacity: 0.86,
          // A last resort for a code with no underscore to break at.
          overflowWrap: 'anywhere',
        }}
      >
        · <StoreCode code={restaurantCode} />
      </Box>
      {language !== 'en' && (
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            whiteSpace: 'nowrap',
            fontSize: 12,
            opacity: 0.7,
          }}
        >
          · {language}
        </Box>
      )}
    </Box>
    <Box
      sx={{
        fontSize: 16,
        fontWeight: 600,
        lineHeight: 1.3,
        overflowWrap: 'anywhere',
      }}
    >
      {question}
    </Box>
  </Box>
);

export default AssistCardHeader;
